import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { T } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import ThemeSelector from './ThemeSelector';
import ExportButton from './Export';
import Responses from './Responses';
import { OECCreatedSource, OECTrainingStatus, TRAINING_STEP } from '../types';
import { getStatus, isAutoTaggingComplete } from '../lib';
import { useGeneral, useRetraining, useResponses, useThemes, useTrainingActions } from '../Context';
import GuidanceBanner from './AutoTagging/GuidanceBanner';
export const COPY = {
  title: {
    id: 'OpenEndedCoding.ManualTagging.title',
    defaultMessage: 'Thematic analysis for question "{questionText}"',
    description: '[Type: Header][Vis: med] - Header for open ended coding view'
  },
  footer: {
    id: 'OpenEndedCoding.ManualTagging.footer',
    defaultMessage: 'Next: Extract keywords',
    description: '[Type: Header][Vis: med] - Button to extract keywords'
  }
};
const useStyles = createUseStyles(theme => {
  // We want to ensure the modal has a fixed height.
  // and doesn't increase in size once responses are loaded.
  const bodyHeight = '90vh';
  return {
    header: {
      borderBottom: `1px solid ${theme.palette.border.main}`,
      padding: `${theme.spacing[6]}px ${theme.spacing[7]}px`,
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
      // On sufficiently large screens
      [`@media (min-width: ${theme.breakpoints.md}px)`]: {
        flexDirection: 'row'
      }
    },
    container: {
      // On sufficiently large screens, hide modal body scrollbar
      [`@media (min-width: ${theme.breakpoints.lg}px)`]: {
        display: 'flex',
        flexFlow: 'column',
        height: '100%'
      }
    },
    themesPanel: {
      // On sufficiently large screens fix the height
      [`@media (min-width: ${theme.breakpoints.lg}px)`]: {
        height: bodyHeight,
        marginLeft: `${theme.spacing[8]}px`,
        overflowY: 'scroll',
        overflowX: 'hidden'
      }
    },
    responsesPanel: {
      // On sufficiently large screens fix the height
      [`@media (min-width: ${theme.breakpoints.lg}px)`]: {
        height: bodyHeight,
        marginRight: `${theme.spacing[8]}px`,
        overflowY: 'scroll',
        overflowX: 'hidden'
      },
      // On sufficiently large screens, show border
      [`@media (min-width: ${theme.breakpoints.md}px)`]: {
        borderLeft: `1px solid ${theme.palette.border.muted}`
      }
    },
    viewVisualization: {
      whiteSpace: 'nowrap'
    },
    buttons: {
      marginTop: `${theme.spacing[4]}px`,
      // On sufficiently large screens
      [`@media (min-width: ${theme.breakpoints.md}px)`]: {
        marginLeft: `${theme.spacing[4]}px`,
        marginTop: 0
      }
    }
  };
});
const Header = ({
  surveyId,
  questionId,
  responsesCount,
  questionText,
  autoTaggingComplete
}) => {
  const classes = useStyles();
  return _jsxs(Box, {
    className: classes.header,
    children: [_jsx(Typography, {
      component: "h4",
      variant: "sectionTitle",
      children: _jsx("strong", {
        children: _jsx(T, {
          desc: COPY.title,
          values: {
            questionText
          }
        })
      })
    }), autoTaggingComplete && _jsx(Box, {
      display: "flex",
      align: "center",
      className: classes.buttons,
      children: _jsx(Box, {
        children: _jsx(ExportButton, {
          surveyId: surveyId,
          questionId: questionId,
          respondentCount: responsesCount
        })
      })
    })]
  });
};
const ManualTagging = ({
  questionText,
  handleClose
}) => {
  const classes = useStyles();
  const {
    surveyId,
    questionId
  } = useGeneral();
  const {
    trainingStatus,
    notEnoughThemesCreated
  } = useRetraining();
  const {
    responsesCount,
    responsesTotal
  } = useResponses();
  const {
    themes,
    themesLoading
  } = useThemes();
  const {
    setShowTrainingScreen,
    setTrainingStep
  } = useTrainingActions();
  const [showDeleteThemeModal, setShowDeleteThemeModal] = useState(false);
  const [showEditThemeModal, setShowEditThemeModal] = useState(false);
  const [showCreateThemeModal, setShowCreateThemeModal] = useState(false);
  const [showCreateSuggestedThemeModal, setShowCreateSuggestedThemeModal] = useState(false);
  const [showChangeUseCaseModal, setShowChangeUseCaseModal] = useState(false);
  const [createdSource, setCreatedSource] = useState(OECCreatedSource.THEMES);
  const status = getStatus({
    responsesTotal,
    themes
  });
  return _jsxs(Box, {
    className: classes.container,
    children: [_jsx(Header, {
      surveyId: surveyId,
      questionId: questionId,
      responsesCount: responsesCount,
      questionText: questionText,
      autoTaggingComplete: isAutoTaggingComplete(trainingStatus),
      handleClose: handleClose
    }), _jsxs(Modal.Body, {
      padding: false,
      children: [trainingStatus === OECTrainingStatus.NOT_TRAINED && !themesLoading && _jsx(GuidanceBanner, {
        hasInsufficientThemes: true
      }), trainingStatus === OECTrainingStatus.REQUIRES_RETRAINING && !themesLoading && _jsx(GuidanceBanner, {
        variant: "update",
        hasInsufficientThemes: notEnoughThemesCreated,
        handleClickUpdateButton: () => {
          setShowTrainingScreen(true);
          setTrainingStep(TRAINING_STEP.KEYWORD_GENERATION);
        }
      }), _jsxs(Grid, {
        noBleeds: true,
        noGutters: true,
        fluid: true,
        children: [_jsx(Grid.Item, {
          xs: 12,
          sm: 12,
          md: 5,
          lg: 4,
          children: _jsx(Box, {
            className: classes.themesPanel,
            children: _jsx(ThemeSelector, {
              createdSource: createdSource,
              showDeleteThemeModal: showDeleteThemeModal,
              showEditThemeModal: showEditThemeModal,
              showCreateThemeModal: showCreateThemeModal,
              showChangeUseCaseModal: showChangeUseCaseModal,
              setCreatedSource: setCreatedSource,
              setShowDeleteThemeModal: setShowDeleteThemeModal,
              setShowEditThemeModal: setShowEditThemeModal,
              setShowCreateThemeModal: setShowCreateThemeModal,
              setShowChangeUseCaseModal: setShowChangeUseCaseModal
            })
          })
        }), _jsx(Grid.Item, {
          xs: 12,
          sm: 12,
          md: 7,
          lg: 8,
          children: _jsx(Box, {
            className: classes.responsesPanel,
            children: _jsx(Responses, {
              showCreateSuggestedThemeModal: showCreateSuggestedThemeModal,
              setShowCreateSuggestedThemeModal: setShowCreateSuggestedThemeModal
            })
          })
        })]
      })]
    }), trainingStatus === OECTrainingStatus.NOT_TRAINED && !themesLoading && _jsx(Modal.Footer, {
      align: "right",
      children: _jsx(Button, {
        disabled: status.notEnoughThemesCreated,
        onClick: () => setShowTrainingScreen(true),
        children: _jsx(T, {
          desc: COPY.footer
        })
      })
    })]
  });
};
export default ManualTagging;